import PropTypes from 'prop-types';
//
import styled from '@emotion/styled';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { Stack, alpha } from '@mui/system';
import { useState } from 'react';
import Iconify from '../../iconify/Iconify';
import Image from '../../image';
import FileCropperDialog from '../cropper/FileCropperDialog';

SingleFilePreview.propTypes = {
  file: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  cropSrc: PropTypes.string,
  cropOpen: PropTypes.bool,
  onCrop: PropTypes.func,
  onCropOpenChange: PropTypes.func,
  onRemove: PropTypes.func,
  lockCropAspectRatio: PropTypes.number,
  sx: PropTypes.object,
  enableEditImage: PropTypes.bool,
  hideRemove: PropTypes.bool,
  showFileName: PropTypes.bool,
  compact: PropTypes.bool,
};

const FilledIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.grey[900], 0.8),
  color: theme.palette.grey[50],
  '&:hover': {
    backgroundColor: alpha(theme.palette.grey[900], 0.9),
  },
}));

export default function SingleFilePreview({
  file,
  cropSrc = '',
  cropOpen = undefined,
  onCrop = () => {},
  onCropOpenChange = () => {},
  onRemove = () => {},
  sx,
  lockCropAspectRatio = null,
  enableEditImage = true,
  hideRemove = false,
  showFileName = false,
  compact = false,
  ...props
}) {
  const [internalCropping, setInternalCropping] = useState(false);
  const isCropControlled = cropOpen !== undefined;
  const cropping = isCropControlled ? cropOpen : internalCropping;

  const setCropping = (next) => {
    if (!isCropControlled) {
      setInternalCropping(next);
    }

    onCropOpenChange(next);
  };

  if (!file) {
    return null;
  }

  const imgUrl = typeof file === 'string' ? file : file.preview;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: compact ? 'flex-start' : 'center',
        gap: compact ? 1 : 3,
        flexDirection: compact ? 'column' : { xs: 'column', sm: 'column', md: 'row' },
      }}
    >
      <Stack
        component={Paper}
        variant="outlined"
        sx={{
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          ...sx,
        }}
        {...props}
      >
        <Image
          alt="file preview"
          src={imgUrl}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        />
        <Stack
          position="absolute"
          height={1}
          width={1}
          justifyContent="center"
          alignItems="center"
          direction="row"
          gap={1}
        >
          {enableEditImage && (
            <FilledIconButton onClick={() => setCropping(true)}>
              <Iconify icon="eva:crop-fill" width={20} height={20} />
            </FilledIconButton>
          )}
          {!hideRemove && (
            <FilledIconButton onClick={() => onRemove(file)}>
              <Iconify icon="eva:trash-2-fill" width={20} height={20} />
            </FilledIconButton>
          )}
        </Stack>
      </Stack>

      {showFileName && (
        <Box
          sx={{
            ...(!compact && { flex: 1 }),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: compact ? 'nowrap' : { xs: 'wrap', md: 'nowrap' },
            ...(compact && { maxWidth: '100%' }),
          }}
        >
          <Typography
            variant={compact ? 'body2' : 'body1'}
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: compact ? 'nowrap' : { xs: 'wrap', md: 'nowrap' },
              ...(compact && { color: 'text.secondary' }),
            }}
          >
            {file.name}
          </Typography>
        </Box>
      )}

      <FileCropperDialog
        src={cropSrc || imgUrl}
        open={cropping}
        onClose={() => setCropping(false)}
        onCrop={onCrop}
        {...(lockCropAspectRatio && { lockAspectRatio: lockCropAspectRatio })}
      />
    </Box>
  );
}
